import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';
import cx from 'classnames';
import { Button, Divider } from '@duxton-web/ui';
import scribeTrack from 'scribe/scribe-track';
import ModalDrawer from 'components/drawer/modal-drawer';
import { inGrabApp } from 'common/utils/platform';
import instrumentationDuck from 'ducks/instrumentation-duck';
import useSearchParams from 'hooks/useSearchParams';
import {
  GRAB_APP_FLAG_NAME,
  GRAB_APP_FLAG_RESULT,
  GRAB_APP_LINK,
} from 'constants/app-constants';

import styles from './driver-card.less';
import { useRTCModule } from '../../hooks/native';
import phoneSvg from '../../public/images/phone.svg';
import chatSvg from '../../public/images/chat.svg';
import IconRating from '../icons/icon-rating';
import chatInAppSvg from '../../public/images/chat-in-app.svg';
import { useFeatureFlag } from '../../context/feature-flag';
import {
  isCancelledStatus,
  isCompletedStatus,
} from '../../common/utils/status';

const DriverInfo = ({ driverInfo, vehicleInfo }) => {
  const { fullname, profileSrc, rating } = driverInfo;
  const { make, model, license } = vehicleInfo;

  return (
    <div className={styles.infoContainer}>
      <div className={styles.imageContainer}>
        <img className={styles.image} src={profileSrc} alt="driver" />
      </div>

      <div className={styles.textContainer}>
        <div className={styles.info}>
          <span className={styles.fullname}>{fullname}</span>
          <span className={styles.rating}>{rating.toFixed(1)}</span>
          <IconRating className={styles.star} rating={rating} />
        </div>

        <div className={styles.vehicleInfo}>
          <div className={styles.model}>
            {make} {model}
          </div>
          <div>&#x2022;</div>
          <div className={styles.license}>{license}</div>
        </div>
      </div>
    </div>
  );
};

DriverInfo.propTypes = {
  driverInfo: PropTypes.shape({
    fullname: PropTypes.string,
    profileSrc: PropTypes.string,
    rating: PropTypes.number,
  }).isRequired,
  vehicleInfo: PropTypes.shape({
    model: PropTypes.string,
    make: PropTypes.string,
    license: PropTypes.string,
  }).isRequired,
};

const DriverContact = ({
  bookingCode,
  phone,
  driverVoip,
  driverName,
  chatRoomId,
  hideChatButton,
}) => {
  const [searchParams] = useSearchParams();
  const { orderId, trackingPageType, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );
  const { callDriver, openChatScreen, inApp } = useRTCModule();

  const [isOpen, setIsOpen] = useState(false);

  const onModalClose = useCallback(() => setIsOpen(false), []);

  const phoneFallback = useCallback(
    () => {
      window.location.replace(`tel:${phone}`);
    },
    [phone],
  );

  const downloadGrabApp = useCallback(() => {
    window.location.href = GRAB_APP_LINK;
  }, []);

  const sendCallInstrumentation = useCallback(
    () => {
      scribeTrack.expressTracking.call({
        trackingSource,
        orderIdDisplayed: orderId,
        isInApp: inGrabApp(),
        trackingPageType,
      });
    },
    [orderId, trackingPageType, trackingSource],
  );

  const sendChatInstrumentation = useCallback(
    () => {
      scribeTrack.expressTracking.chat({
        trackingSource,
        orderIdDisplayed: orderId,
        isInApp: inGrabApp(),
        trackingPageType,
      });
    },
    [orderId, trackingPageType, trackingSource],
  );

  const onPhoneClick = useCallback(
    async () => {
      sendCallInstrumentation();
      if (!driverVoip || !inApp) {
        phoneFallback();
        return;
      }

      const { isError: isCallError } = await callDriver({
        bookingCode,
        driverVoipId: driverVoip,
        phoneNumber: phone,
        calleeName: driverName,
      });

      if (isCallError) {
        phoneFallback();
      }
    },
    [sendCallInstrumentation, bookingCode, driverVoip, phone, inApp],
  );

  const onOpenInbox = useCallback(
    async () => {
      sendChatInstrumentation();
      if (
        !inApp &&
        searchParams.get(GRAB_APP_FLAG_NAME) !== GRAB_APP_FLAG_RESULT
      ) {
        setIsOpen(true);
        return;
      }

      const { isError: isOpenChatScreenError } = await openChatScreen({
        bookingCode,
        chatId: chatRoomId,
      });

      if (isOpenChatScreenError) {
        const errorCtx = rumErrorCtx()
          .component('DriverContact.onOpenInbox')
          .message('Failed to open chat screen')
          .severity(SEVERITY.MEDIUM)
          .custom('bookingCode', bookingCode)
          .custom('chatRoomId', chatRoomId)
          .build();

        ddRum.addError(new Error('Chat screen failed to open'), errorCtx);
      }
    },
    [
      sendChatInstrumentation,
      bookingCode,
      chatRoomId,
      inApp,
      openChatScreen,
      searchParams,
    ],
  );

  return (
    <div className={styles.contactContainer}>
      <Button
        aria-label="call"
        className={styles.actionButton}
        onClick={onPhoneClick}
        size="small"
      >
        <img src={phoneSvg} alt="phone" />
      </Button>

      {!hideChatButton && (
        <Divider className={styles.dividerVertical} type="vertical" />
      )}

      {!hideChatButton && (
        <Button
          aria-label="open inbox"
          className={styles.actionButton}
          onClick={onOpenInbox}
          size="small"
        >
          <img src={chatSvg} alt="chat" />
        </Button>
      )}

      <ModalDrawer
        open={isOpen}
        onClose={onModalClose}
        buttonText={I18n.t('downloadApp.button')}
        content={
          <div className={styles.downloadAppContainer}>
            <img src={chatInAppSvg} alt="chat in app" />
            <p className={styles.heading}>{I18n.t('downloadApp.heading')}</p>

            <p className={styles.body}>{I18n.t('downloadApp.body')}</p>
          </div>
        }
        onSubmit={downloadGrabApp}
      />
    </div>
  );
};

DriverContact.propTypes = {
  bookingCode: PropTypes.string,
  driverVoip: PropTypes.string,
  driverName: PropTypes.string,
  phone: PropTypes.string,
  chatRoomId: PropTypes.string,
  hideChatButton: PropTypes.bool,
};

DriverContact.defaultProps = {
  bookingCode: '',
  driverVoip: '',
  driverName: '',
  phone: '',
  chatRoomId: '',
  hideChatButton: false,
};

const DriverCard = ({
  bookingCode,
  driverInfo,
  vehicleInfo,
  contact,
  status,
  userType,
}) => {
  const { hideCallAndChat } = useFeatureFlag();
  const { isSender, isReceiver } = userType;
  const isGrabUser = isSender || isReceiver;

  const hideContact = hideCallAndChat || !inGrabApp() || !isGrabUser;
  const hideChatButton = useMemo(
    () => isCancelledStatus(status) || isCompletedStatus(status),
    [status],
  );

  const containerClassName = useMemo(
    () =>
      cx(styles.container, {
        [styles.flexContainer]: hideChatButton,
      }),
    [hideChatButton],
  );

  return (
    <div className={containerClassName}>
      <DriverInfo driverInfo={driverInfo} vehicleInfo={vehicleInfo} />

      {!hideContact &&
        !hideChatButton && <Divider className={styles.dividerHorizontal} />}

      {!hideContact && (
        <DriverContact
          bookingCode={bookingCode}
          phone={contact.phone}
          chatRoomId={contact.chatRoomId}
          driverVoip={contact.voipId}
          driverName={driverInfo.fullname}
          hideChatButton={hideChatButton}
        />
      )}
    </div>
  );
};

DriverCard.propTypes = {
  bookingCode: PropTypes.string,
  driverInfo: PropTypes.shape({
    fullname: PropTypes.string,
    profileSrc: PropTypes.string,
    rating: PropTypes.number,
  }).isRequired,
  vehicleInfo: PropTypes.shape({
    model: PropTypes.string,
    make: PropTypes.string,
    license: PropTypes.string,
  }).isRequired,
  contact: PropTypes.shape({
    phone: PropTypes.string,
    voipId: PropTypes.string,
    chatRoomId: PropTypes.string,
  }),
  status: PropTypes.string,
  userType: PropTypes.shape({
    isSender: PropTypes.bool,
    isReceiver: PropTypes.bool,
  }),
};

DriverCard.defaultProps = {
  bookingCode: '',
  contact: {
    phone: '',
    voipId: '',
    chatRoomId: '',
  },
  status: '',
  userType: {
    isSender: false,
    isReceiver: false,
  },
};

export default DriverCard;
